<template>
    <div class="w-[5.5rem] relative customHeight flex flex-col">

        <section
            class="fixed top-2 left-2 customHeight gridContainerDribble surface-nav bg-card-primary py-4 gap-y-0 rounded-r-lg drop-shadow-md h-auto custom-scrollbar"
            :class="{ sideNavClosed: useIsNavClosed }" ref="navbar" v-click-outside="handleClickOutside">
            <div class="!justify-self-center row-start-1 col-start-1 h-[2.5rem]">
                <div class="h-[2.5rem] w-[30px] relative top-[-2px] flex justify-center">
                    <NuxtImg src="/img/pnlogo.svg" alt="Dashboard Logo" class="h-full w-auto" />
                </div>
            </div>

            <div
                class="row-start-1 col-start-2 h-[2.5rem] overflow-hidden text-text-primary text-nowrap content-center mb-4 leading-tight">
                <p class="pr-6 flex flex-col"><span class="text-[14px] font-bold ">ADMIN</span><span
                        class="text-sm leading-none">DASHBOARD</span></p>
            </div>
            <div
                class=" justify-self-end absolute col-start-2 row-start-1 aspect-square h-[40px] bg-card-primary top-[-6px] right-[-20px] z-10 rounded-r-full flex justify-center items-center">
                <div class="cursor-pointer flex justify-center items-center bg-primary hover:bg-primary/90 rounded-full "
                    @click="openCloseHandler">
                    <UIcon :class="useIsNavClosed ? '' : 'rotate-180'"
                        class="w-6 h-6 group-hover/sideNav:text-dashPrimary text-white  hover:text-white"
                        name="i-heroicons-chevron-right-16-solid" />

                </div>
            </div>

            <!-- <SideNavigationDropDownClientID v-if="runtimeConfig.public.isDevelopment"></SideNavigationDropDownClientID> -->

            <SideNavigationSearchBtn
                v-if="links && (runtimeConfig.public.isDevelopment || !runtimeConfig.public.vercelProd)"
                @searchResults="searchResults" :links="links"></SideNavigationSearchBtn>

            <div class="grid grid-cols-subgrid col-span-full overflow-auto custom-scrollbar">
                <SideNavigationNavItem
                    v-for="link in activeNavigation"
                    :key="link.label"
                    :link="link"
                    :unfold-sub-item="unfoldSubItem"
                />
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()

import type { Website } from '~/types/api/client/index'
import type { ClientList } from '~/types/api/user';
import type { Navigation, NavigationResponse } from "~/types/api/navigation";

import { API } from '~/utils/api';
const api = new API()
const route = useRoute();

const useSelectedClient = useState<ClientList>('selectedClient');
const useSelectedWebsite = useState<Website>('selectedWebsite');

const { data: links, error } = await useAsyncData(
    'getLinksForNavigation-v2',
    async () => {
        const clientId = useSelectedClient.value?.client_id || 123
        const websiteId = useSelectedWebsite.value?.id || 123
        return await api.client(clientId).website(websiteId).getLinks() || []
    },
    {
        watch: [useSelectedClient, useSelectedWebsite],
        immediate: true,
    },
)

if (error.value) {
    if (error.value.statusCode >= 400) {
        await navigateTo(`/login?redirect_uri=${encodeURIComponent(route.path)}`)
    }
    throw createError({
        statusCode: error.value.statusCode,
        statusMessage: error.value.statusMessage,
    })
}

const searchResult = shallowRef<Navigation[]>([]);

const useIsNavClosed = useState('isNavClosed', () => true)
const unfoldSubItem = ref(false);

const compiledNavigation = computed(() => {
    if (searchResult.value.length) {
        return searchResult.value
    } else {
        return links.value
    }
})

const searchResults = (results: Navigation[]) => {
    if (results.length > 0) {
        unfoldSubItem.value = true;
    } else {
        unfoldSubItem.value = false;
    }
    searchResult.value = results;

    if (results == links.value) {
        searchResult.value = []
    }
}


// WIP - Visually show which link is active
const activeNavigation = computed(() => {
    let modifiedPath = route?.matched[0]?.path;
    // console.log('modifiedPath',modifiedPath)

    // Handle nested route parameters
    if (route.params) {
        Object.entries(route.params).forEach(([key, value]) => {
            const paramPattern = new RegExp(`:${key}\\(\\)`);
            if (paramPattern.test(modifiedPath)) {
                modifiedPath = modifiedPath.replace(paramPattern, value as string);
            }
        });
    }

    if (compiledNavigation.value == null) {
        return []
    }
    // Create a new array with updated isActive properties
    return compiledNavigation.value.map(navItem => ({
        ...navItem,
        isActive: navItem.to === modifiedPath,
        subitems: navItem.subitems?.map(subItem => ({
            ...subItem,
            isActive: subItem.to === modifiedPath
        }))
    }));
});

// console.log("activeNavigation",activeNavigation.value)

watch(
    () => route.fullPath,
    () => {
        useIsNavClosed.value = true;
    }
)

const openCloseHandler = () => {
    useIsNavClosed.value = !useIsNavClosed.value
}


interface HTMLElementClickOutside extends HTMLElement {
    __ClickOutsideHandler__: (event: MouseEvent) => void;
}
const handleClickOutside = () => useIsNavClosed.value = true;
const vClickOutside = {
    mounted(el: HTMLElementClickOutside, binding: DirectiveBinding<any>): void {
        el.__ClickOutsideHandler__ = (event: MouseEvent) => {
            if (!(el === event.target || el.contains(event.target as Node))) {
                binding.value(event)
            }
        }
        document.body.addEventListener("click", el.__ClickOutsideHandler__)
    },
    unmounted(el: HTMLElementClickOutside): void {
        document.body.removeEventListener("click", el.__ClickOutsideHandler__)
    }
}

</script>

<style lang="scss" scoped>
.gridContainerDribble {
    display: grid;
    grid-template-columns: 4.5rem 1fr;
    // grid-auto-rows: max-content;
    grid-auto-rows: auto;
    transition: grid-template-columns .3s ease;
    align-content: flex-start;
}

.sideNavClosed {
    grid-template-columns: 4.5rem 0fr;
}

.customHeight {
    height: calc(100svh - 2rem);
}

.custom-scrollbar {
    overscroll-behavior: contain;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: var(--ui-bg-elevated);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--ui-border-accented);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--ui-border);
    }
}
</style>